/**
 * Pure (React-free) helpers for the copy / cut / paste mechanism on the
 * crew schedule creator (CrewSchedule.tsx, step "Crew schedules").
 *
 * Kept side-effect free so the time math and the "relocate an assignment
 * to another crew member / time slot" transformation are unit-testable in
 * isolation — mirroring the existing lib/syncAssignmentTimes.ts pattern.
 *
 * Time strings follow the same festival-day frame as CrewSchedule: the
 * timeline runs 09:00 → 09:00 next day, so an hour < 9 (e.g. "01:00")
 * represents the early morning of the following day. Durations are computed
 * on raw clock minutes (matching handleAssignmentDrop) and the resulting end
 * time is normalised back into a valid 24h "HH:MM" — never an invalid
 * "25:00" — so it is safe to persist to crew_assignments.
 */

export type ClipboardMode = "copy" | "cut";

/** Minimal shape a value needs to be relocated by {@link rebaseAssignment}. */
export interface RebasableAssignment {
  id?: string;
  crew_member_id: string;
  event_date?: string;
  filming_start: string;
  filming_end: string;
  assignment_type: "artist" | "custom";
  artist_name?: string;
  lineup_id?: string | null;
  subject?: string;
}

export interface RebaseTarget {
  /** Crew member the pasted block lands on. */
  crewMemberId: string;
  /** Snapped start time, "HH:MM". */
  startTime: string;
  /** Festival day the pasted block belongs to, "YYYY-MM-DD". */
  eventDate?: string;
}

export interface RebaseOptions {
  /**
   * Convert an artist-type assignment into a custom one. The caller sets
   * this when the source is bound to an artist that isn't on the target
   * day's lineup, so the pasted block stays visible (it would otherwise
   * render into a non-existent artist row).
   */
  convertToCustom?: boolean;
}

const pad = (n: number): string => n.toString().padStart(2, "0");

/** Minutes since midnight for an "HH:MM" or "HH:MM:SS" string. */
export function toMinutes(time: string): number {
  const [h, m] = time.split(":");
  return parseInt(h, 10) * 60 + parseInt(m, 10);
}

/** Strip seconds: "13:00:00" → "13:00". Pads single-digit fields. */
export function normalizeTime(time: string): string {
  const [h, m] = time.split(":");
  return `${pad(parseInt(h, 10))}:${pad(parseInt(m, 10))}`;
}

/**
 * Clock-minute duration between two times. Mirrors the raw-hour subtraction
 * in handleAssignmentDrop so a relocated block keeps the exact width it had.
 */
export function durationMinutes(start: string, end: string): number {
  return toMinutes(end) - toMinutes(start);
}

/**
 * Add `minutes` to a start time and return a valid "HH:MM". Wraps a result
 * of 24:00 or later back into the next-day range (e.g. 24:30 → 00:30),
 * matching positionToTime's normalisation, so the value is always storable.
 */
export function shiftTime(start: string, minutes: number): string {
  let total = toMinutes(start) + minutes;
  // Keep within a single 0..47h window before folding the >=24h tail back.
  if (total < 0) total = 0;
  let hours = Math.floor(total / 60);
  const mins = total % 60;
  if (hours >= 24) hours -= 24;
  return `${pad(hours)}:${pad(mins)}`;
}

/**
 * Produce a new assignment positioned on `target`'s crew member and start
 * time, preserving the original block's duration, type and bindings
 * (subject / stage / notes / lineup_id). The source `id` is intentionally
 * dropped so a paste is always a brand-new row rather than a move/update of
 * the original.
 */
export function rebaseAssignment<T extends RebasableAssignment>(
  assignment: T,
  target: RebaseTarget,
  options: RebaseOptions = {},
): T {
  const duration = durationMinutes(assignment.filming_start, assignment.filming_end);
  const filmingStart = normalizeTime(target.startTime);
  const filmingEnd = shiftTime(filmingStart, duration);

  // Strip id — the paste is a new assignment, not a mutation of the source.
  const { id: _id, ...rest } = assignment;

  const next: RebasableAssignment = {
    ...rest,
    crew_member_id: target.crewMemberId,
    filming_start: filmingStart,
    filming_end: filmingEnd,
  };

  if (target.eventDate) {
    next.event_date = target.eventDate;
  }

  if (options.convertToCustom && assignment.assignment_type === "artist") {
    next.assignment_type = "custom";
    next.subject = assignment.subject || assignment.artist_name || "Custom";
    next.artist_name = undefined;
    next.lineup_id = undefined;
  }

  return next as T;
}

/**
 * Clamp an artist assignment's end to the artist's set end so it can never run
 * past the set. Returns the (possibly shortened) end plus flags:
 * - `shortened`: the end was pulled back to the set end.
 * - `impossible`: the start is already at/after the set end — nothing fits.
 * Times compare on raw clock minutes, matching the timeline's overlap math.
 */
export function clampEndToSetEnd(
  filmingStart: string,
  filmingEnd: string,
  setEnd: string,
): { end: string; shortened: boolean; impossible: boolean } {
  const setEndMinutes = toMinutes(setEnd);
  if (toMinutes(filmingEnd) <= setEndMinutes) {
    return { end: normalizeTime(filmingEnd), shortened: false, impossible: false };
  }
  if (toMinutes(filmingStart) >= setEndMinutes) {
    return { end: normalizeTime(filmingEnd), shortened: false, impossible: true };
  }
  return { end: normalizeTime(setEnd), shortened: true, impossible: false };
}

/** The lineup fields a paste needs to bind an assignment to a specific artist slot. */
export interface ArtistBinding {
  artist_name: string;
  stage_name: string;
  stage_id?: string | null;
  lineup_id?: string | null;
}

/**
 * Re-point an assignment at a different artist slot, keeping everything else
 * (crew member, times, notes) intact. Used when pasting a copied block onto
 * an artist row: the producer is assigning that crew member to *this* artist,
 * so the binding follows the drop target rather than the source.
 */
/**
 * The binding that ties an assignment to one lineup slot.
 *
 * `lineup_id` is the load-bearing field: the schedule rows match an
 * assignment to its artist on lineup_id FIRST (falling back to
 * artist_name + stage_name only for legacy rows without one). Rebinding
 * without it leaves the block pointing at the slot it came from, so it keeps
 * rendering under its old artist — the move looks like it did nothing.
 * Derived here, in one place, so every caller that moves a block onto an
 * artist (drag-and-drop, paste) binds it the same way.
 *
 * `resolveStageId` must be day-scoped: stage labels repeat across festival
 * days, and an unscoped lookup attaches another day's stage_id.
 */
export function artistBinding(
  artist: { id?: string; artist_name: string; stage_name: string },
  resolveStageId: (label?: string) => string | null,
): ArtistBinding {
  return {
    artist_name: artist.artist_name,
    stage_name: artist.stage_name,
    stage_id: resolveStageId(artist.stage_name),
    lineup_id: artist.id ?? null,
  };
}

export function rebindToArtist<
  T extends RebasableAssignment & { stage_name?: string; stage_id?: string | null },
>(assignment: T, binding: ArtistBinding): T {
  return {
    ...assignment,
    assignment_type: "artist",
    artist_name: binding.artist_name,
    stage_name: binding.stage_name,
    stage_id: binding.stage_id ?? null,
    lineup_id: binding.lineup_id ?? null,
  };
}

/**
 * Re-bind an assignment to a stage as a stage-bound custom duty (the
 * stage-duties strip): drops any artist binding, keeps crew member, times,
 * subject and notes. Used when pasting a copied block into a stage's
 * duties strip.
 */
export function rebindToStage<
  T extends RebasableAssignment & { stage_name?: string; stage_id?: string | null },
>(assignment: T, stage: { id: string; label: string }): T {
  return {
    ...assignment,
    assignment_type: "custom",
    // A converted artist block has no subject of its own — name the duty
    // after the artist (matching commitPaste's convertToCustom fallback) so
    // the strip never shows an unnamed duty.
    subject: assignment.subject || assignment.artist_name || "Custom",
    artist_name: undefined,
    lineup_id: null,
    stage_id: stage.id,
    stage_name: stage.label,
  };
}
